import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import ClusterApi from '../../api/ClusterApi';

// Types
import Cluster from '../../types/cluster/Cluster';
import AvailabilityGroupType from '../../types/cluster/AvailabilityGroup';
import AvailabilityReplica from '../../types/cluster/AvailabilityReplica';
import AvailabilityDatabase from '../../types/cluster/AvailabilityDatabase';
import AvailabilityListener from '../../types/cluster/AvailabilityListener';
import EventsDataTable from '../../types/instance/tables/EventsDataTable';

// Components
import ClustersTabbed from './ClustersTabbed';
import AgTopology from '../../component/cluster/AgTopology';
import ReplicasTable from '../../component/cluster/table/ReplicasTable';
import EventsTable from '../../component/instance/table/EventsTable';
import HealthBadge from '../../component/cluster/HealthBadge';

// Constants
import { DATA_LOADED, DATA_LOADING } from '../../component/Constants';

// Contexts
import { TimeRangeContext } from '../../context/TimeRangeContext';

dayjs.extend(utc);
dayjs.extend(timezone);

interface IAvailabilityGroupProps {
    toggleMenu: Function
}

const AvailabilityGroup: React.FC<IAvailabilityGroupProps> = (props) => {
    const { clusterId, groupId } = useParams();
    const [loading, setLoading] = useState<number>(DATA_LOADING);
    const [refreshCounter, setRefreshCounter] = useState<number>(0);
    const timeRangeContext = useContext(TimeRangeContext);

    const [cluster, setCluster] = useState<Cluster | undefined>(undefined);
    const [group, setGroup] = useState<AvailabilityGroupType | undefined>(undefined);
    const [replicas, setReplicas] = useState<AvailabilityReplica[]>([]);
    const [databases, setDatabases] = useState<AvailabilityDatabase[]>([]);
    const [listener, setListener] = useState<AvailabilityListener | undefined>(undefined);
    const [events, setEvents] = useState<EventsDataTable[]>([]);

    useEffect(() => {
        let active = true;
        const id = Number(clusterId);
        const gid = Number(groupId);

        async function load() {
            setLoading(DATA_LOADING);
            const [clusterResult, groupsResult, replicasResult, databasesResult, listenersResult, eventsResult] =
                await Promise.all([
                    ClusterApi.getCluster(id),
                    ClusterApi.getAvailabilityGroups(id),
                    ClusterApi.getReplicas(gid),
                    ClusterApi.getDatabases(gid, timeRangeContext.timeRange),
                    ClusterApi.getListeners(id),
                    ClusterApi.getEvents(id, timeRangeContext.timeRange, gid),
                ]);
            if (!active) return;

            const groupResult = groupsResult.find(item => item.id === gid);
            setCluster(clusterResult);
            setGroup(groupResult);
            setReplicas(replicasResult);
            setDatabases(databasesResult);
            setListener(listenersResult.find(item => item.groupid === gid));
            setEvents(eventsResult);
            setLoading(DATA_LOADED);
        }

        void load();
        return () => {
            active = false;
        };
    }, [clusterId, groupId, refreshCounter, timeRangeContext]);

    const refresh = () => {
        setLoading(DATA_LOADING);
        setRefreshCounter(counter => counter + 1);
    };

    return (
        <ClustersTabbed
            clusterId={Number(clusterId)}
            clusterName={cluster?.name ?? ''}
            groupName={group?.name ?? ''}
            toggleMenu={props.toggleMenu}
            refresh={refresh}
            loading={loading}
            activeTab="group"
            title={`${group?.name ?? 'Availability group'} - DBmarlin`}
            description="Availability group overview with replicas and databases">

            {/* Group summary strip */}
            <div className="row row-cols-1">
                <div className="col">
                    <div className="card">
                        <div className="card-body">
                            <span className="me-3">
                                <span className="text-muted me-1">Health:</span>
                                {group && <HealthBadge value={group.health}/>}
                            </span>
                            <span className="me-3">
                                <span className="text-muted me-1">Primary:</span>
                                {group?.primaryreplica}
                            </span>
                            <span className="me-3">
                                <span className="text-muted me-1">Failover:</span>
                                {group?.failovermode === 'AUTOMATIC' ? 'Automatic' : 'Manual'}
                            </span>
                            <span className="me-3">
                                <span className="text-muted me-1">Automatic failover readiness:</span>
                                {group?.failoverready === null
                                    ? <HealthBadge value="UNKNOWN"/>
                                    : group?.failoverready
                                        ? <span className="badge bg-success">READY</span>
                                        : <span className="badge bg-warning text-dark">NOT READY</span>}
                            </span>
                            {/* No sparkline, so no rate-cell layout: flow control lives
                                on the replica state table, which has no time series
                                endpoint. Any series here would be permanently empty
                                and would read as a measured flat line. */}
                            <span className="me-3">
                                <span className="text-muted me-1">Flow control:</span>
                                {group?.flowcontrolms === undefined
                                    ? <span className="text-muted"
                                            data-tooltip-id="global-tooltip"
                                            data-tooltip-content="No monitored instance is sending to a secondary in this group">&ndash;</span>
                                    : <span
                                        data-tooltip-id="global-tooltip"
                                        data-tooltip-content="Worst time the primary spent throttled sending to a secondary">{`${group.flowcontrolms.toFixed(1)} ms/s`}</span>}
                            </span>
                            {group?.stale &&
                                <div className="text-muted mt-2">
                                    <i className="fal fa-exclamation-triangle fa-fw amber-icon me-1" aria-hidden="true"/>
                                    {group.lastsample === undefined
                                        ? 'No recent data from the monitored instances'
                                        : `No data from the monitored instances since ${dayjs(group.lastsample)
                                            .tz(timeRangeContext.timeRange.timezone).format('YYYY-MM-DD HH:mm')}`}
                                    , so states are shown as unknown.
                                </div>}
                        </div>
                    </div>
                </div>
            </div>

            {/* Topology */}
            <div className="row row-cols-1">
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-project-diagram fa-fw" aria-hidden="true"/>
                            Topology
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                               data-bs-target="#collapseTopology" aria-expanded="false" aria-controls="collapseTopology"/>
                        </div>
                        <div id="collapseTopology" className="card-body collapse show p-0">
                            {group && <AgTopology group={group} replicas={replicas} listener={listener}/>}
                        </div>
                    </div>
                </div>
            </div>

            {/* Replicas and databases */}
            <div className="row row-cols-1">
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-clone fa-fw" aria-hidden="true"/>
                            Availability replicas
                            <span className="badge bg-info">{replicas.length}</span>
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                               data-bs-target="#collapseReplicas" aria-expanded="false" aria-controls="collapseReplicas"/>
                        </div>
                        <div id="collapseReplicas" className="card-body collapse show">
                            <ReplicasTable replicas={replicas} databases={databases}/>
                        </div>
                    </div>
                </div>
            </div>

            {/* Events for this group */}
            <div className="row row-cols-1">
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-exchange fa-fw" aria-hidden="true"/>
                            Events
                            <Link className="ms-2 small" to="/admin/alert-rules">Manage rules</Link>
                            <span className="badge bg-info">{events.length}</span>
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                               data-bs-target="#collapseEvents" aria-expanded="false" aria-controls="collapseEvents"/>
                        </div>
                        <div id="collapseEvents" className="card-body collapse show">
                            <EventsTable loading={loading} eventsDataTable={events} hideRegister={true}/>
                        </div>
                    </div>
                </div>
            </div>
        </ClustersTabbed>
    );
};

export default AvailabilityGroup;
